import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, switchMap, of, catchError, throwError, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AuthTokenResponse,
  ChangePasswordPayload,
  ClientAuthSession,
  ClientAuthSessionListResponse,
  GoogleRedirectResponse,
} from '../models/client-auth-session.model';
import { extractApiList } from '../utils/api-list.util';
import { alignGoogleOAuthRedirectUrl, extractOriginalGoogleState, getGoogleCallbackUrl } from '../utils/google-oauth.util';
import { SKIP_GLOBAL_ERROR_REDIRECT } from '../interceptors/error.interceptor';
import { formatHttpErrorMessage } from '../utils/http-error.util';
import { isUserEmailVerified } from '../utils/email-verification.util';
import { ti } from '../i18n/translate-instant';

export interface RegisterRequest {
    name: string;
    email: string;
    password: string;
}

export interface Establishment {
    id: number;
    name: string;
    slug: string;
    partner_id: number;
    type: string;
    description: string;
    city: string;
    status: string;
    adress: string;
    phone: string;
    email: string;
    website: string;
    lat: string;
    lng: string;
    admin_note: string;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
}

export interface User {
    id: number;
    name: string;
    email: string;
    email_verified_at: string;
    is_verified?: boolean;
    email_verified?: boolean;
    partner_status: string;
    password_updated_at: string;
    created_at: string;
    updated_at: string;
    account_type: string;
    is_partner: boolean;
    has_establishment: boolean;
    is_active: boolean;
    establishment_count: number;
    establishments: Establishment[];
    has_password?: boolean;
    provider?: string | null;
    phone?: string | null;
    status?: string;
    avatar_url?: string | null;
    photo_url?: string | null;
    avatar?: string | null;
    is_pending_deletion?: boolean;
    deletion_scheduled_at?: string | null;
    days_remaining_until_purge?: number | { [key: string]: any } | null;
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private http = inject(HttpClient);
    private router = inject(Router);
    private apiUrl = `${environment.apiUrl}/v1/auth`;
    private logoutTimer: ReturnType<typeof setTimeout> | null = null;

    // State management using Signals
    public currentUser = signal<User | null>(this.getUserFromStorage());
    /** Tracked separately so `isAuthenticated` reacts when the token is written/cleared. */
    private sessionToken = signal<string | null>(
        typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null,
    );
    public isAuthenticated = computed(() => {
        const user = this.currentUser();
        const token = this.sessionToken();
        if (!user || !token || this.isTokenExpired(token)) {
            return false;
        }
        return isUserEmailVerified(user);
    });

    constructor() {
        queueMicrotask(() => this.bootstrapSession());
    }

    private bootstrapSession(): void {
        const token = localStorage.getItem('token');
        this.sessionToken.set(token);

        if (!token) {
            return;
        }

        if (this.isTokenExpired(token)) {
            this.clearSession();
            return;
        }

        this.scheduleAutoLogout(token);
        this.getUserInfo().subscribe();
    }

    /**
     * Re-read token/user from storage into signals.
     * Needed after SPA login→home: storage is warm but the client shell can mount
     * against a stale in-memory auth view until a full reload.
     */
    hydrateSessionFromStorage(): void {
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
        this.sessionToken.set(token);

        if (!token || this.isTokenExpired(token)) {
            return;
        }

        const stored = this.getUserFromStorage();
        if (stored) {
            const verified = this.ensureAuthenticatedUser(stored);
            if (verified) {
                this.currentUser.set(verified);
                localStorage.setItem('currentUser', JSON.stringify(verified));
            }
        }

        this.scheduleAutoLogout(token);
    }

    private getUserFromStorage(): User | null {
        const token = localStorage.getItem('token');
        const storedUser = localStorage.getItem('currentUser');

        if (!token || !storedUser) {
            return null;
        }

        if (this.isTokenExpired(token)) {
            localStorage.removeItem('token');
            localStorage.removeItem('currentUser');
            return null;
        }

        try {
            return JSON.parse(storedUser) as User;
        } catch {
            localStorage.removeItem('currentUser');
            return null;
        }
    }

    private hasStoredTokenExpired(): boolean {
        const token = localStorage.getItem('token');
        return !!token && this.isTokenExpired(token);
    }

    /** JWT-shaped tokens are three base64url segments. Opaque Sanctum PATs are not. */
    private isJwtShapedToken(token: string): boolean {
        const parts = String(token || '').split('.');
        return parts.length === 3 && parts.every((part) => part.length > 0);
    }

    private isTokenExpired(token: string): boolean {
        // Opaque Sanctum tokens have no client-side exp — rely on API 401.
        if (!this.isJwtShapedToken(token)) {
            return false;
        }

        const expiresAt = this.getTokenExpiration(token);
        // JWT-shaped without a usable `exp` (missing or unreadable) → treat as expired.
        if (expiresAt === null) {
            return true;
        }

        return Date.now() >= expiresAt;
    }

    private getTokenExpiration(token: string): number | null {
        try {
            const payload = token.split('.')[1];
            if (!payload) {
                return null;
            }

            const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
            const decodedPayload = atob(
                normalizedPayload.padEnd(Math.ceil(normalizedPayload.length / 4) * 4, '=')
            );
            const parsedPayload = JSON.parse(decodedPayload);

            return typeof parsedPayload.exp === 'number' ? parsedPayload.exp * 1000 : null;
        } catch {
            return null;
        }
    }

    private scheduleAutoLogout(token: string): void {
        if (this.logoutTimer) {
            clearTimeout(this.logoutTimer);
            this.logoutTimer = null;
        }

        const expiresAt = this.getTokenExpiration(token);
        if (!expiresAt) {
            return;
        }

        this.logoutTimer = setTimeout(() => {
            this.clearSession(true);
        }, Math.max(expiresAt - Date.now(), 0));
    }

    clearSession(redirectToLogin = true): void {
        if (this.logoutTimer) {
            clearTimeout(this.logoutTimer);
            this.logoutTimer = null;
        }

        localStorage.removeItem('token');
        localStorage.removeItem('currentUser');
        // Never leave an admin impersonation stash after logout/401 — restoring it later
        // would escalate privileges on the next login in the same browser.
        localStorage.removeItem('original_admin_token');
        localStorage.removeItem('pendingVerificationToken');
        localStorage.removeItem('pendingVerificationEmail');
        this.sessionToken.set(null);
        this.currentUser.set(null);

        if (redirectToLogin) {
            this.redirectToLoginIfNeeded();
        }
    }

    private redirectToLoginIfNeeded(): void {
        const url = this.router.url;

        if (
            url.startsWith('/manager') ||
            url.startsWith('/admin') ||
            url.startsWith('/client/') ||
            url.startsWith('/booking')
        ) {
            this.router.navigate(['/auth/login'], { queryParams: { returnUrl: url } });
        }
    }

    signOut(): void {
        this.clearSession(false);
    }

    private getValidToken(): string | null {
        const token = localStorage.getItem('token');

        if (!token) {
            return null;
        }

        if (this.isTokenExpired(token)) {
            this.clearSession(true);
            return null;
        }

        return token;
    }

    storePendingVerificationToken(token?: string | null, email?: string | null): void {
        if (token) {
            localStorage.setItem('pendingVerificationToken', token);
        }
        if (email) {
            localStorage.setItem('pendingVerificationEmail', email.trim());
        }
    }

    private persistAuthSession(token: string, user: User | null): void {
        localStorage.setItem('token', token);
        localStorage.removeItem('pendingVerificationToken');
        this.sessionToken.set(token);
        this.scheduleAutoLogout(token);

        if (user) {
            localStorage.setItem('currentUser', JSON.stringify(user));
            this.currentUser.set(user);
        }
    }

    /**
     * Successful password/2FA login already means the API accepted the account.
     * Login payloads (and some `/auth/user` shapes) often omit verification flags;
     * without them `isAuthenticated` stays false and the client shell looks logged out.
     */
    private ensureAuthenticatedUser(user: User | null | undefined): User | null {
        if (!user || typeof user !== 'object') {
            return null;
        }
        if (isUserEmailVerified(user)) {
            return user;
        }
        const verifiedAt =
            typeof user.email_verified_at === 'string' && user.email_verified_at.trim()
                ? user.email_verified_at
                : new Date().toISOString();
        return {
            ...user,
            is_verified: true,
            email_verified: true,
            email_verified_at: verifiedAt,
        };
    }

    private extractUserPayload(response: unknown): User | null {
        if (!response || typeof response !== 'object') {
            return null;
        }
        const record = response as Record<string, unknown>;
        const data = (record['data'] ?? record) as Record<string, unknown> | null;
        if (!data || typeof data !== 'object') {
            return null;
        }
        if ('email' in data || 'id' in data || 'account_type' in data) {
            return data as unknown as User;
        }
        return null;
    }

    updateCurrentUser(partial: Partial<User>): void {
        const current = this.currentUser();
        if (!current) return;
        const updated = { ...current, ...partial };
        localStorage.setItem('currentUser', JSON.stringify(updated));
        this.currentUser.set(updated);
    }

    setCurrentUser(user: User): void {
        localStorage.setItem('currentUser', JSON.stringify(user));
        this.currentUser.set(user);
    }

    register(data: RegisterRequest): Observable<any> {
        return this.http.post<any>(`${this.apiUrl}/register`, data).pipe(
            tap((response) => {
                if (response?.success && response?.data?.token) {
                    const user = response.data.user ?? null;
                    this.persistAuthSession(response.data.token, user);
                    // Re-store after persistAuthSession (it clears pendingVerificationToken).
                    // Kept so verification can restore the session if `token` is cleared later.
                    this.storePendingVerificationToken(response.data.token, data.email);
                }
            })
        );
    }

    /**
     * After a successful email verification, restore/persist the session and mark the
     * user as verified so `isAuthenticated` becomes true immediately.
     */
    activateVerifiedSession(options: {
        token?: string | null;
        user?: Partial<User> | null;
        email?: string | null;
    } = {}): Observable<User | null> {
        const token =
            options.token
            || localStorage.getItem('token')
            || localStorage.getItem('pendingVerificationToken');

        if (!token) {
            return of(null);
        }

        const existing = this.currentUser();
        const verifiedAt = new Date().toISOString();
        const baseUser = (options.user || existing || {
            id: 0,
            name: '',
            email: options.email || localStorage.getItem('pendingVerificationEmail') || '',
            email_verified_at: verifiedAt,
            partner_status: '',
            password_updated_at: '',
            created_at: '',
            updated_at: '',
            account_type: 'user',
            is_partner: false,
            has_establishment: false,
            is_active: true,
            establishment_count: 0,
            establishments: [],
        }) as User;

        const verifiedUser: User = {
            ...baseUser,
            ...(options.user || {}),
            email: (options.user?.email || baseUser.email || options.email || '').trim(),
            is_verified: true,
            email_verified: true,
            email_verified_at:
                (options.user?.email_verified_at as string | undefined)
                || baseUser.email_verified_at
                || verifiedAt,
        };

        this.persistAuthSession(token, verifiedUser);
        localStorage.removeItem('pendingVerificationToken');
        localStorage.removeItem('pendingVerificationEmail');

        return this.getUserInfo().pipe(
            map((response) => {
                const apiUser = (response?.data ?? response) as User | null;
                if (apiUser && typeof apiUser === 'object' && 'email' in apiUser) {
                    const merged: User = {
                        ...verifiedUser,
                        ...apiUser,
                        is_verified: true,
                        email_verified: true,
                        email_verified_at:
                            apiUser.email_verified_at
                            || verifiedUser.email_verified_at
                            || verifiedAt,
                    };
                    // getUserInfo may clear the session on 401 — always re-assert verified session.
                    this.persistAuthSession(token, merged);
                    return merged;
                }
                this.persistAuthSession(token, verifiedUser);
                return verifiedUser;
            }),
            catchError(() => {
                this.persistAuthSession(token, verifiedUser);
                return of(verifiedUser);
            }),
        );
    }

    checkEmail(email: string): Observable<{ exists: boolean }> {
        return this.http.post<any>(
            `${this.apiUrl}/check-email`,
            { email },
            { context: new HttpContext().set(SKIP_GLOBAL_ERROR_REDIRECT, true) },
        ).pipe(
            map((response) => {
                const data = response?.data ?? response;
                return { exists: data?.exists === true };
            }),
        );
    }

    login(credentials: { email: string, password: string }): Observable<any> {
        return this.http.post<any>(`${this.apiUrl}/login`, credentials).pipe(
            switchMap(response => {
                const data = response?.data ?? response;
                const authStatus = data?.auth_status || response?.auth_status;

                // 2FA Challenge Case: DO NOT persist session or call getUserInfo yet
                if (
                    authStatus === 'TWO_FACTOR_REQUIRED' ||
                    data?.requires_2fa ||
                    data?.two_factor_required ||
                    (data?.user && (data.user.is_two_factor_enabled || data.user.two_factor_enabled))
                ) {
                    return of(response);
                }

                const loginToken =
                    response?.data?.token
                    || response?.data?.access_token
                    || data?.token
                    || data?.access_token;

                if (response.success && loginToken) {
                    const token = String(loginToken);
                    const fallbackUser = this.ensureAuthenticatedUser(
                        response.data?.user ?? data?.user ?? null,
                    );
                    this.persistAuthSession(token, fallbackUser);

                    return this.getUserInfo().pipe(
                        map((userInfo) => {
                            const apiUser = this.extractUserPayload(userInfo);
                            if (apiUser) {
                                const merged = this.ensureAuthenticatedUser({
                                    ...(fallbackUser || {}),
                                    ...apiUser,
                                } as User);
                                // Re-assert after getUserInfo — it clears the session on 401.
                                this.persistAuthSession(token, merged);
                                return userInfo;
                            }

                            this.persistAuthSession(token, fallbackUser);
                            return fallbackUser
                                ? { success: true, data: fallbackUser }
                                : response;
                        }),
                        catchError(() => {
                            this.persistAuthSession(token, fallbackUser);
                            return of(
                                fallbackUser
                                    ? { success: true, data: fallbackUser }
                                    : response,
                            );
                        }),
                    );
                }

                return of(response);
            })
        );
    }

    getUserInfo(): Observable<any> {
        const token = this.getValidToken();

        if (!token) {
            return of(null);
        }

        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

        return this.http.get<any>(`${this.apiUrl}/user`, { headers }).pipe(
            tap(response => {
                const apiUser = this.extractUserPayload(response);
                if (!apiUser) {
                    return;
                }
                const token = localStorage.getItem('token');
                const verified = this.ensureAuthenticatedUser({
                    ...(this.currentUser() || {}),
                    ...apiUser,
                } as User);
                if (token && verified) {
                    this.persistAuthSession(token, verified);
                } else if (verified) {
                    localStorage.setItem('currentUser', JSON.stringify(verified));
                    this.currentUser.set(verified);
                }
            }),
            catchError(error => {
                // Do not wipe a just-established login here — the auth interceptor
                // handles hard 401s on authenticated API calls. Clearing in this
                // catch raced SPA post-login navigation and left the home shell logged out.
                if (error?.status !== 401) {
                    console.error('User Info Error:', error);
                }

                return of(null);
            })
        );
    }

    logout() {
        const token = localStorage.getItem('token');
        if (token && !this.isTokenExpired(token)) {
            const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
            this.http.post(`${this.apiUrl}/logout`, {}, { headers }).subscribe({
                error: (err) => console.error('Logout API error', err)
            });
        }

        // Always clear local state even if API call fails or there is no token
        this.clearSession(true);
    }

    becomePartner(data: any): Observable<any> {
        const token = localStorage.getItem('token');
        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
        return this.http.post(`${environment.apiUrl}/v1/partner/become-partner`, data, { headers });
    }

    /** Optimistically mark the session as a pending partner after a successful application. */
    markPartnerApplicationSubmitted(): void {
        const current = this.currentUser();
        if (!current) {
            return;
        }

        // API keeps account_type as "user"; partner access is driven by partner_status.
        const updated: User = {
            ...current,
            is_partner: true,
            partner_status: 'PENDING',
        };

        localStorage.setItem('currentUser', JSON.stringify(updated));
        this.currentUser.set(updated);
    }

    verifyEmail(id: string, hash: string, signedParams: Record<string, string> = {}): Observable<any> {
        return this.http.get(`${this.apiUrl}/email/verify/${id}/${hash}`, { params: signedParams });
    }

    persistSession(token: string, user: User | null): void {
        this.persistAuthSession(token, this.ensureAuthenticatedUser(user));
    }

    resendVerificationEmail(email?: string): Observable<any> {
        const token = this.getValidToken();

        if (!token) {
            return throwError(() => ({
                status: 401,
                error: {
                    message: ti('ERRORS.AUTH.RESEND_VERIFICATION_SESSION'),
                },
            }));
        }

        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
        const body = email ? { email } : {};

        return this.http.post(`${this.apiUrl}/email/verification-notification`, body, { headers });
    }

    forgotPassword(email: string): Observable<any> {
        return this.http.post(`${this.apiUrl}/password/email`, { email });
    }

    resetPassword(data: { token: string, email: string, password: string }): Observable<any> {
        return this.http.post(`${this.apiUrl}/password/reset`, data);
    }

    updateProfile(data: { name: string }): Observable<any> {
        const token = this.getValidToken();

        if (!token) {
            return of(null);
        }

        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

        return this.http.patch<any>(`${environment.apiUrl}/v1/profile`, data, { headers }).pipe(
            tap(response => {
                if (response?.success && response?.data) {
                    localStorage.setItem('currentUser', JSON.stringify(response.data));
                    this.currentUser.set(response.data);
                } else if (response?.data) {
                    localStorage.setItem('currentUser', JSON.stringify(response.data));
                    this.currentUser.set(response.data);
                } else if (data.name) {
                    const current = this.currentUser();
                    if (current) {
                        const updated = { ...current, name: data.name };
                        localStorage.setItem('currentUser', JSON.stringify(updated));
                        this.currentUser.set(updated);
                    }
                }
            }),
            catchError(error => {
                console.error('Update profile error:', error);
                return throwError(() => new Error(formatHttpErrorMessage(error, 'ERRORS.AUTH.UPDATE_PROFILE')));
            })
        );
    }

    getSessions(): Observable<ClientAuthSession[]> {
        const token = this.getValidToken();
        if (!token) {
            return of([]);
        }

        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

        return this.http.get<ClientAuthSessionListResponse>(`${this.apiUrl}/sessions`, { headers }).pipe(
            map((response) => {
                const items = Array.isArray(response?.data)
                    ? response.data
                    : extractApiList<Record<string, unknown>>(response?.data);

                return items.map((item) => this.normalizeSession(item));
            }),
            catchError(() => of([])),
        );
    }

    revokeSession(tokenId: string): Observable<boolean> {
        const token = this.getValidToken();
        if (!token) {
            return of(false);
        }

        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

        return this.http.delete<any>(`${this.apiUrl}/sessions/${tokenId}`, { headers }).pipe(
            map((response) => Boolean(response?.success ?? true)),
            catchError(() => of(false)),
        );
    }

    revokeOtherSessions(): Observable<boolean> {
        const token = this.getValidToken();
        if (!token) {
            return of(false);
        }

        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

        return this.http.delete<any>(`${this.apiUrl}/sessions/others`, { headers }).pipe(
            map((response) => Boolean(response?.success ?? true)),
            catchError(() => of(false)),
        );
    }

    logoutAllDevices(): Observable<boolean> {
        const token = localStorage.getItem('token');
        if (!token || this.isTokenExpired(token)) {
            this.clearSession(true);
            return of(true);
        }

        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

        return this.http.post<any>(`${this.apiUrl}/logout-all`, {}, { headers }).pipe(
            map((response) => Boolean(response?.success ?? true)),
            tap(() => this.clearSession(true)),
            catchError(() => {
                this.clearSession(true);
                return of(true);
            }),
        );
    }

    refreshToken(): Observable<User | null> {
        const token = this.getValidToken();
        if (!token) {
            return of(null);
        }

        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

        return this.http.post<AuthTokenResponse>(`${this.apiUrl}/refresh`, {}, { headers }).pipe(
            switchMap((response) => this.applyTokenAuthResponse(response)),
            catchError(() => {
                this.clearSession(true);
                return of(null);
            }),
        );
    }

    changePassword(payload: ChangePasswordPayload): Observable<boolean> {
        const token = this.getValidToken();
        if (!token) {
            return throwError(() => new Error(ti('ERRORS.AUTH.MUST_LOGIN_PASSWORD')));
        }

        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
        const body: Record<string, any> = {
            password: payload.password,
            password_confirmation: payload.password,
        };

        if (payload.current_password) {
            body['current_password'] = payload.current_password;
        }

        return this.http.put<any>(`${environment.apiUrl}/v1/profile/password`, body, { headers }).pipe(
            tap(() => {
                const user = this.currentUser();
                if (user && user.has_password === false) {
                    const updated = { ...user, has_password: true };
                    localStorage.setItem('currentUser', JSON.stringify(updated));
                    this.currentUser.set(updated);
                }
            }),
            map((response) => Boolean(response?.success ?? true)),
            catchError((error) =>
                throwError(() => new Error(formatHttpErrorMessage(error, 'ERRORS.AUTH.CHANGE_PASSWORD'))),
            ),
        );
    }

    getGoogleRedirectUrl(): Observable<string> {
        return this.http.get<GoogleRedirectResponse>(`${this.apiUrl}/google/redirect`).pipe(
            map((response) => {
                const redirectUrl = response?.data?.redirect_url;
                if (!redirectUrl) {
                    throw new Error(response?.message || ti('ERRORS.AUTH.GOOGLE_START'));
                }
                return redirectUrl;
            }),
            catchError((error) =>
                throwError(() => new Error(formatHttpErrorMessage(error, 'ERRORS.AUTH.GOOGLE_START'))),
            ),
        );
    }

    /**
     * @deprecated Token-in-URL Google auth is disabled (security). Use `completeGoogleAuth` with `code`.
     */
    completeGoogleAuthWithToken(_token: string): Observable<User | null> {
        return throwError(
            () => new Error(ti('ERRORS.AUTH.GOOGLE_TOKEN_DISABLED')),
        );
    }

    completeGoogleAuth(queryParams: Record<string, string>): Observable<User | null> {
        let params = new HttpParams();
        Object.entries(queryParams).forEach(([key, value]) => {
            if (value && key !== 'redirect_uri' && key !== 'state') {
                params = params.set(key, value);
            }
        });

        return this.http.get<AuthTokenResponse>(`${this.apiUrl}/google/callback`, { params }).pipe(
            switchMap((response) => this.applyTokenAuthResponse(response)),
            catchError((error) =>
                throwError(() => new Error(formatHttpErrorMessage(error, 'ERRORS.AUTH.GOOGLE_FAILED'))),
            ),
        );
    }

    startGoogleLogin(): void {
        this.getGoogleRedirectUrl().subscribe({
            next: (redirectUrl) => {
                window.location.href = redirectUrl;
            },
            error: (error) => {
                console.error('Google OAuth redirect failed', error);
            },
        });
    }

    private applyTokenAuthResponse(response: AuthTokenResponse): Observable<User | null> {
        const token = response?.data?.token;
        const user = response?.data?.user as User | undefined;

        if (!token) {
            return throwError(() => new Error(response?.message || ti('ERRORS.AUTH.AUTH_IMPOSSIBLE')));
        }

        this.persistAuthSession(token, user ?? null);

        if (user) {
            return of(user);
        }

        return this.getUserInfo().pipe(
            map((userInfo) => userInfo?.data ?? userInfo ?? null),
        );
    }

    private normalizeSession(raw: Record<string, unknown>): ClientAuthSession {
        return {
            id: String(raw['id'] ?? raw['token_id'] ?? ''),
            name: raw['name'] != null ? String(raw['name']) : null,
            last_used_at: raw['last_used_at'] != null ? String(raw['last_used_at']) : null,
            created_at: String(raw['created_at'] ?? ''),
            expires_at: raw['expires_at'] != null ? String(raw['expires_at']) : null,
            is_current: Boolean(raw['is_current']),
        };
    }
}
